import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, ArrowRight, KeyRound } from 'lucide-react';
import { Button } from '../components/common';

export default function ForgotPasswordPage() {
  const { forgotPassword, confirmResetPassword } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' | 'confirm'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleRequestCode(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setStep('confirm');
    } catch (err) {
      setError(err.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmResetPassword(email, code, newPassword);
      navigate('/auth/login', { state: { passwordReset: true } });
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-bg safe-area-top safe-area-bottom">
      <div className="flex flex-col items-center justify-start min-h-screen px-4 py-12 max-w-md mx-auto">
        <div className="text-center mb-8 mt-12">
          <div className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mb-3" aria-hidden="true">
            <KeyRound size={28} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-night-text">
            {step === 'request' ? 'Reset password' : 'Enter new password'}
          </h1>
          <p className="text-sm text-gray-500 dark:text-night-muted mt-1">
            {step === 'request'
              ? 'Enter your email to receive a reset code'
              : `Enter the code sent to ${email}`}
          </p>
        </div>

        <div className="w-full bg-white dark:bg-night-surface rounded-2xl shadow-sm border border-gray-100 dark:border-night-card p-5 space-y-4">
          {error && (
            <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {step === 'request' ? (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-night-text mb-1">Email</span>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-night-muted" aria-hidden="true" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoComplete="email"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </label>
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Sending…' : 'Send Reset Code'}
                {!loading && <ArrowRight size={18} aria-hidden="true" />}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-night-text mb-1">Verification code</span>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="000000"
                  required
                  maxLength={6}
                  inputMode="numeric"
                  className="w-full px-3 py-3 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-center text-2xl tracking-[0.5em] font-mono text-gray-900 dark:text-night-text placeholder-gray-300 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-night-text mb-1">New password</span>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-night-muted" aria-hidden="true" />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </label>
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Resetting…' : 'Reset Password'}
                {!loading && <ArrowRight size={18} aria-hidden="true" />}
              </Button>
            </form>
          )}
        </div>

        <p className="text-sm text-gray-500 dark:text-night-muted mt-6">
          <Link to="/auth/login" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
